import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "@/redux/type";
import { useEffect, useState } from "react";
import axios from "axios";
import { config } from "@/config";
import { setCustomer } from "@/redux/actions/customerAction";
import { setProgram } from "@/redux/actions/programAction";

export default function ProgramGateway() {
    const dispatch = useDispatch();
    const location = useLocation();
    const session = useSelector((state: ReduxState) => state.session);
    const program = useSelector((state: ReduxState) => state.program);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState("");
    const hasProgramContext = !!program?.role && !!program?.programId && !!program?.personId;
    const isCompleteProfilePage = location.pathname === "/complete-profile";
    const defaultPath = program.role === "admin" ? "/rewards-claims" : "/profile";

    useEffect(() => {
        if (!hasProgramContext) return;
        if (program.role !== "external_sales") {
            dispatch(setProgram({ profileCompleted: true }));
            return;
        }
        if (program.profileCompleted !== null && program.profileCompleted !== undefined) return;
        if (!session.sessionToken || !program.programId) return;

        let alive = true;
        setLoading(true);
        setLoadError("");

        axios.get(`${config.backendUrl}/users/profile`, {
            headers: {
                Authorization: `Bearer ${session.sessionToken}`,
                programId: program.programId
            }
        }).then((response) => {
            if (!alive) return;
            const data = response.data || {};
            const profileCompleted = !!String(data?.ssnCode || "").trim() && Array.isArray(data?.files) && data.files.length > 0;
            setLoading(false);
            dispatch(setCustomer(data?.customer || { cardCode: undefined, name: undefined }));
            dispatch(setProgram({ profileCompleted }));
        }).catch((error: any) => {
            if (!alive) return;
            setLoading(false);
            setLoadError(error.response?.data?.message || "Failed to load user profile");
        });

        return () => {
            alive = false;
        };
    }, [dispatch, hasProgramContext, program.profileCompleted, program.programId, program.role, session.sessionToken]);

    if (!hasProgramContext) {
        return <Navigate to="/select-program" replace />;
    }

    if (program.role === "external_sales" && loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (program.role === "external_sales" && loadError) {
        return (
            <div className="flex min-h-[50vh] items-center justify-center px-4">
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">
                    {loadError}
                </div>
            </div>
        );
    }

    if (program.role === "external_sales" && program.profileCompleted === false && !isCompleteProfilePage) {
        return <Navigate to="/complete-profile" replace />;
    }

    if (isCompleteProfilePage && (program.role !== "external_sales" || program.profileCompleted === true)) {
        return <Navigate to={defaultPath} replace />;
    }

    return <Outlet />;
}
